<template>
  <CPopover
    v-if="selected"
    :class="{
      '-top-1': placement.includes('top'),
      '-bottom-1': !placement.includes('top'),
    }"
    :placement="placement"
    :show="show"
    :show-arrow="false"
    :style="{ '--n-padding': 0 }"
    :to="to ? to : isScribbleChild ? false : '.canvas-builder-app'"
    trigger="manual"
    :z-index="10"
  >
    <template #trigger>
      <div
        :class="[
          `absolute drag-handle cursor-grab z-4 left-0 ${placement.includes('top') ? '-top-3' : '-bottom-3'} flex items-center justify-center w-full`,
        ]"
      >
        <div class="flex items-center justify-center w-8 h-6 pa-1 border border-solid border-info rounded-2 bg-info2">
          <CIcon class="color-info" icon="ellipsis" />
        </div>
      </div>
    </template>
    <div
      v-if="hasActions"
      class="editable-component__actions flex items-center b-rounded-2 relative bg-primary6 shadow-md pa-1"
    >
      <CButton
        v-if="draggable"
        v-tooltip="t('canvasUI.canvasBuilder.components.editableComponent.drag')"
        class="editable-component__actions__drag-handle w-9 h-9"
        text
        @mousedown.stop="emit('startDrag')"
      >
        <template #icon>
          <CIcon icon="grip-dots-vertical" size="16" />
        </template>
      </CButton>
      <CButton
        v-if="add"
        v-tooltip="t('canvasUI.canvasBuilder.components.editableComponent.add')"
        class="w-9 h-9"
        text
        @click.stop="emit('add')"
      >
        <template #icon>
          <CIcon :icon="addIcon || 'plus'" size="16" />
        </template>
      </CButton>
      <CButton
        v-if="edit"
        v-tooltip="t('canvasUI.canvasBuilder.components.editableComponent.edit')"
        class="w-9 h-9"
        text
        @click.stop="emit('edit')"
      >
        <template #icon>
          <CIcon :icon="editIcon || 'pen'" size="16" />
        </template>
      </CButton>
      <CButton
        v-if="settings"
        v-tooltip="t('canvasUI.canvasBuilder.components.editableComponent.settings')"
        class="w-9 h-9"
        text
        @click.stop="emit('settings')"
      >
        <template #icon>
          <CIcon :icon="settingsIcon || 'cog'" size="16" />
        </template>
      </CButton>
      <template v-if="stylable">
        <NPopover placement="bottom-start" trigger="click" :width="280">
          <template #trigger>
            <CButton
              v-tooltip="t('canvasUI.canvasBuilder.components.editableComponent.theme')"
              class="w-9 h-9"
              text
              @click.stop="emit('toggleThemeOptions')"
            >
              <template #icon>
                <CIcon icon="palette" size="16" />
              </template>
            </CButton>
          </template>
          <CComponentThemeOptions
            :component-node="componentNode"
            :exclude-stylables="excludeStylables"
            :model-value="themeOptions"
            @choose-content="chooseContent"
            @set-palette-index="emit('set-palette-index', $event)"
            @update:modelValue="onThemeUpdate"
          />
        </NPopover>
      </template>
      <CButton
        v-if="duplicate"
        v-tooltip="t('canvasUI.canvasBuilder.components.editableComponent.duplicate')"
        class="w-9 h-9"
        text
        @click.stop="emit('duplicate')"
      >
        <template #icon>
          <CIcon :icon="duplicateIcon || 'copy'" size="16" />
        </template>
      </CButton>
      <CButton
        v-if="discard"
        v-tooltip="t('canvasUI.canvasBuilder.components.editableComponent.revert')"
        class="w-9 h-9"
        text
        @click.stop="emit('discard')"
      >
        <template #icon>
          <CIcon :icon="discardIcon || 'clock-rotate-left'" size="16" />
        </template>
      </CButton>
      <CButton
        v-if="debuggable && componentNode"
        v-tooltip="t('canvasUI.canvasBuilder.components.editableComponent.inspectJson')"
        class="w-9 h-9"
        text
        @click.stop="emit('toggleInspectNode')"
      >
        <template #icon>
          <CIcon icon="display-code" size="16" />
        </template>
      </CButton>

      <slot name="toolbar-slot" />

      <NDropdown
        v-if="variants"
        :options="variants.options"
        placement="bottom-start"
        trigger="click"
        :value="variants.selected"
        @select="emit('variant-change', $event)"
      >
        <CButton
          v-if="selectedVariant"
          icon-placement="left"
          size="small"
          :style="{ fontFamily: 'var(--font-family)' }"
        >
          {{ selectedVariant.label }}
          <template #icon>
            <Component :is="selectedVariant.icon" class="mr-1" />
          </template>
        </CButton>
        <CButton v-else class="mr-2" size="small">{{ variants.title }}</CButton>
      </NDropdown>

      <div
        v-if="remove || collapsible"
        :class="{
          'flex ml-1 border-primary5 border-l border-l-solid pl-1': showActionSeparator,
        }"
      >
        <CButton
          v-if="remove"
          v-tooltip="t('canvasUI.canvasBuilder.components.editableComponent.delete')"
          class="w-9 h-9"
          text
          @click.stop="emit('remove')"
        >
          <template #icon>
            <CIcon :color="themeVars.error" :icon="removeIcon || 'trash'" size="16" />
          </template>
        </CButton>
        <CButton
          v-if="collapsible"
          v-tooltip="t('canvasUI.canvasBuilder.components.editableComponent.toggleCollapsible')"
          class="w-9 h-9"
          text
          @click.stop="emit('toggleCollapse')"
        >
          <template #icon>
            <CIcon :icon="isCollapsed ? 'arrows-from-line' : 'arrows-to-line'" size="16" />
          </template>
        </CButton>
      </div>
    </div>
  </CPopover>
</template>

<script setup lang="ts">
import { computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { NDropdown, NPopover } from 'naive-ui'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CComponentThemeOptions from '@lib/components/CComponentThemeOptions/CComponentThemeOptions.vue'
import useCanvasTheme, {
  type ThemeOptions,
  type ThemeComponentOptionEnum,
  type ThemePadding,
  type ThemeBorderRadius,
} from '@canvas-builder/composables/useCanvasTheme'
import useThemeVars from '@lib/composables/useThemeVars'
import { ComponentTypes, type ComponentNode, type ScribbleProps } from '@canvas-builder/types/canvas'
import type { DropdownOption, PopoverPlacement } from 'naive-ui'
import { FileContentTypeEnum } from '@openapi'
import type { UiSelectContentRequest, UiSelectContentResponse } from '@sdk/api/modules/ui/types.ui'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { SelectorType } from '@content-selector/stores/app'

const props = defineProps<{
  selected: boolean
  placement: PopoverPlacement
  show: boolean
  draggable: boolean
  add: boolean
  edit: boolean
  settings: boolean
  stylable: boolean
  duplicate: boolean
  discard: boolean
  debuggable: boolean
  remove: boolean
  collapsible: boolean
  isCollapsed: boolean
  isInspectingNode: boolean
  addIcon?: string
  editIcon?: string
  settingsIcon?: string
  duplicateIcon?: string
  discardIcon?: string
  removeIcon?: string
  variants?: {
    title: string
    options: DropdownOption[]
    selected?: DropdownOption['key']
  }
  componentNode?: ComponentNode
  excludeStylables?: ThemeComponentOptionEnum[]
  padding: ThemePadding
  spacing: number
  showActionSeparator: boolean
  parentType?: ComponentTypes
  borderRadius: ThemeBorderRadius
  to?: HTMLElement | string
}>()

const emit = defineEmits<{
  startDrag: []
  add: []
  edit: []
  settings: []
  toggleThemeOptions: []
  'bg-property-change': [property: string, value: any]
  'content-change': [{ fileId: string; pageIndex?: number; pageId?: string; thumbnailUrl?: string }]
  'asset-change': [{ assetId: string }]
  updateHeight: [height?: string | null]
  updatePadding: [padding: ThemePadding]
  updateMarginBottom: [value: number | null]
  resetSpacing: []
  duplicate: []
  discard: []
  toggleInspectNode: []
  'variant-change': [variant: DropdownOption['key']]
  remove: []
  toggleCollapse: []
  'update-border-radius': [borderRadius: ThemeBorderRadius]
  'set-palette-index': [index: number | undefined]
}>()

// const selectContent = inject('selectUserContent') as (options: any) => Promise<any>
const selectContent =
  inject<(payload?: UiSelectContentRequest) => Promise<UiSelectContentResponse>>('selectUserContent')

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { t } = useI18n()
const { handleComponentThemeUpdate } = useCanvasTheme()
const themeVars = useThemeVars()
const hasActions = computed(
  () =>
    props.draggable ||
    props.add ||
    props.edit ||
    props.settings ||
    props.stylable ||
    props.duplicate ||
    props.discard ||
    (props.debuggable && props.componentNode) ||
    props.variants ||
    props.remove ||
    props.collapsible,
)

const isScribbleChild = computed(() => {
  return props.parentType === ComponentTypes.Scribble
})

const selectedVariant = computed(() => props.variants?.options.find((i) => i.key === props.variants?.selected))

async function chooseContent() {
  if (!selectContent) {
    console.error('selectContent is not provided')
    return
  }

  try {
    const allowedSelectorTypes: SelectorType[] = ['assets', 'external_links', 'ai_images', 'pexels_images', 'theme']
    const allowedContentTypes: FileContentTypeEnum[] = []
    if (launchDarkly.value.enable_scribble_content_as_background) {
      allowedSelectorTypes.push('content')
      allowedContentTypes.push(FileContentTypeEnum.PDF)
    }

    const data = await selectContent({
      allowed_selector_types: allowedSelectorTypes,
      max_selections: 1,
      allowed_asset_content_types: [FileContentTypeEnum.IMAGE],
      only_allow_content_pdf_pages: !!launchDarkly.value.enable_scribble_content_as_background,
      allowed_content_types: allowedContentTypes,
    })

    const fileId = (data?.content?.[0] as any)?.file?.id
    const pageIndex = (data?.content?.[0] as any)?.page_index ?? 0
    const thumbnailUrl = (data?.content?.[0] as any)?.thumbnail_url
    const pageId = (data?.content?.[0] as any)?.metadata?.pptx_data?.slides?.[pageIndex]?.id
    const type = (data?.content?.[0] as any)?.type

    if (launchDarkly.value.enable_scribble_content_as_background && type === 'page' && fileId) {
      emit('content-change', { fileId, pageIndex, pageId, thumbnailUrl })
    } else {
      const newImageUrl = data?.content?.[0]?.thumbnail_url ?? (data?.content?.[0] as any)?.content_url

      if (newImageUrl) {
        emit('bg-property-change', 'backgroundImage', newImageUrl)

        // Only emit asset-change event if the user selected an asset type
        if (type === 'asset' && fileId) {
          emit('asset-change', { assetId: fileId })
        }
      }
    }
  } catch (error) {
    console.error('Error in chooseContent:', error)
  }
}

function onThemeUpdate(updates: ThemeOptions) {
  handleComponentThemeUpdate(updates, themeOptions.value, {
    onBackgroundChange: (property, value) => {
      emit('bg-property-change', property, value)

      // Also clear content_background for Scribble components when clearing background image
      if (
        property === 'backgroundImage' &&
        value === null &&
        props.componentNode?.type === ComponentTypes.Scribble &&
        launchDarkly.value.enable_scribble_content_as_background
      ) {
        emit('content-change', { fileId: '' })
      }
    },
    onHeightChange: (height) => emit('updateHeight', height),
    onPaddingChange: (padding) => emit('updatePadding', padding),
    onMarginBottomChange: (value) => emit('updateMarginBottom', value),
    onResetSpacing: () => emit('resetSpacing'),
    onBorderRadiusChange: (borderRadius) => emit('update-border-radius', borderRadius),
  })
}

const themeOptions = computed(() => {
  const isScribble = props.componentNode?.type === ComponentTypes.Scribble
  const scribbleProps = props.componentNode?.data as ScribbleProps
  const isFileDeleted =
    isScribble && (scribbleProps?.content_background?.is_deleted || scribbleProps?.asset_background?.is_deleted)

  let backgroundImage = props.componentNode?.style?.backgroundImage
  if (
    isScribble &&
    !backgroundImage &&
    launchDarkly.value.enable_scribble_content_as_background &&
    (props.componentNode?.data as any)?.content_background?.thumbnail_url
  ) {
    backgroundImage = `url("${(props.componentNode.data as any).content_background.thumbnail_url}")`
  }

  if (isFileDeleted) {
    backgroundImage = ''
  }

  return {
    backgroundColor: props.componentNode?.style?.backgroundColor,
    backgroundAttachment: props.componentNode?.style?.backgroundAttachment,
    backgroundImage,
    backgroundPositionX: props.componentNode?.style?.backgroundPositionX,
    backgroundPositionY: props.componentNode?.style?.backgroundPositionY,
    backgroundRepeat: props.componentNode?.style?.backgroundRepeat,
    backgroundSize: props.componentNode?.style?.backgroundSize,
    height: props.componentNode?.style?.height,
    padding: props.padding,
    spacing: props.spacing,
    borderRadius: props.borderRadius,
  }
})
</script>
